/**
 * Cross-page deep-link contract.
 * ------------------------------
 * The SINGLE source of truth for "what happens when you land on a page via a
 * URL": the query-param vocabulary, the `ref` provenance enum, the builders that
 * construct a link (to one case in Followups, or to /cases with filters), and the
 * parsers each target page uses to read its "arrival intent".
 *
 * Why this exists: the same link is built in ~8 places (case cards, dashboard,
 * audit log, case import, followups report, /cases, /calendar) with three
 * different mechanisms (router push, `window.location.href`, raw URL strings) and
 * scattered magic-string params. One drift = a silently broken deep-link. And the
 * RULES a page applies on arrival (view-as handoff vs bypass, filter hydration)
 * used to live as inline `route.query.ref === '...'` comparisons. Centralising
 * both here keeps the builders and readers in lock-step and turns the arrival
 * rules into named, testable flags.
 *
 * Pure functions (no Vue reactivity) so they're importable anywhere, including
 * non-setup contexts (a `window.location.href` string builder, a table cell).
 *
 * LEGACY params: the retired user-tag vocabulary (`userTag`, `viewAsTag`) is no
 * longer read or written — old bookmarks carrying them still open their page
 * (the params are simply ignored; "a URL always reaches its page").
 */

/** Query-param keys shared across pages. */
export const DL = {
  // Followups deep-link: which company / lead-agency / case to open.
  company: 'cid',
  agency: 'aid',
  case: 'caid',

  // Optional follow-up to scroll-into-view + pulse-highlight in the list.
  followUp: 'fuid',

  // Where the link came from (drives the view-as rule on Followups arrival).
  ref: 'ref',

  // /cases → Followups handoff: view AS this user (the case list's assignee
  // filter carries over as a read-only View-As scope).
  viewAsUser: 'viewAsUser',

  // /calendar → /cases filter handoff.
  status: 'status',
  assignedTo: 'assignedTo',
  caseTag: 'caseTag',
  planningDate: 'planningDate',

  // The company FILTER of /cases and /calendar (repeatable: `?companyFilter=1&companyFilter=2`).
  // Not `company`/`cid`: those open a company's Followups, a different thing.
  companyFilter: 'companyFilter',

  // Which face of the Followups panel to land on: 'chat' = the case SMS chat
  // (the panel's flip side), 'notes' = the record. Absent still means notes,
  // for links written before the value was explicit.
  view: 'v',

  // /contact-messages: which contact to open, and (optional) the category door
  // the sidebar narrows to on arrival (Sandy 2026-10-05) — any source can pass
  // it; today the /contacts card does.
  contact: 'contactId',
  contactCategory: 'cat',

  // /staffing/followups: open the staff member's Documents modal on arrival
  // (the system note "Documents received by email" links here, 2026-10-12).
  documents: 'docs',
}

/** The /contact-messages door for contacts with no category. */
export const DL_UNCATEGORIZED = 'uncategorized'

export const DL_VIEW_CHAT = 'chat'

/**
 * Notes, SAID OUT LOUD (Sandy 2026-08-18). It used to be the absence of `v`,
 * which meant a link could not tell "open the notes" apart from "didn't say" —
 * and a card clicked in the Messages block, arriving with nothing, landed on
 * whichever face the page happened to be on.
 */
export const DL_VIEW_NOTES = 'notes'

/**
 * WHERE a Followups deep-link originated. Only `currentCases` carries an
 * assignee handoff (view AS that user); every other source is a plain deep-link
 * to one case, so view-as is CLEARED on arrival and the case is always
 * reachable. Add a new entry here rather than hard-coding a new string at a
 * call site.
 */
export const DL_REF = {
  currentCases: 'current-cases',
  calendar: 'calendar',
  dashboard: 'dashboard',
  notifications: 'notifications',
  webNotification: 'web-notification',
  headerAlerts: 'header-alerts',
  log: 'log',
  search: 'search',
  chatAlert: 'chat-alert',

  // The unified inbox's case cards (Sandy 2026-08-14) — a FOREIGN deep link
  // (full filter reset on arrival), and it says where the click came from.
  inbox: 'inbox',
}

// ── Builders (source pages) ──────────────────────────────────────────────────

/**
 * Vue Router location to open ONE case in Followups. `ref` says where it came
 * from; `viewAsUserId` (only from /cases) hands off the view-as scope. Omitted
 * params are dropped so the URL stays clean.
 *
 * THE FACE ALWAYS TRAVELS (Sandy 2026-08-18). `view` defaults to `notes`, so
 * every link this builds carries `v=` — the fifteen call sites that never
 * passed one produced URLs that said nothing about which side to open, and the
 * page then landed on whichever face it happened to be showing. Wanting the
 * chat is the thing you say out loud; wanting the record is the default, and
 * now it's written down rather than implied by an absence.
 */
export function followupsCaseRoute({
  companyId = null,
  agencyId = null,
  caseId = null,
  followUpId = null,
  ref = null,
  viewAsUserId = null,
  view = null,
} = {}) {
  return {
    path: '/company-cases',
    query: {
      ...(companyId != null ? { [DL.company]: String(companyId) } : {}),
      ...(agencyId != null ? { [DL.agency]: String(agencyId) } : {}),
      ...(caseId != null ? { [DL.case]: String(caseId) } : {}),
      ...(followUpId != null ? { [DL.followUp]: String(followUpId) } : {}),
      ...(ref ? { [DL.ref]: ref } : {}),
      ...(viewAsUserId != null ? { [DL.viewAsUser]: String(viewAsUserId) } : {}),
      [DL.view]: String(view || DL_VIEW_NOTES),
    },
  }
}

/**
 * String-URL form of {@link followupsCaseRoute} — for `window.location.href`
 * (full reload), copy-to-clipboard links and absolute (emailed) links.
 * `absolute` prefixes the production origin.
 */
export function followupsCaseUrl(opts = {}, { absolute = false } = {}) {
  const { path, query } = followupsCaseRoute(opts)
  const qs = new URLSearchParams(query).toString()
  const rel = qs ? `${path}?${qs}` : path

  return absolute ? `https://abaintake.com${rel}` : rel
}

/**
 * Vue Router location to /cases carrying the calendar's filters. On arrival
 * /cases MOVES exactly these and resets the rest (see {@link readCasesArrival}).
 */
export function casesFilterRoute({
  status = 'active',
  assignedUserId = null,
  caseTagIds = null,
  companyIds = null,
  planningDate = null,
} = {}) {
  return {
    path: '/cases',
    query: {
      [DL.status]: status,
      ...(assignedUserId != null ? { [DL.assignedTo]: String(assignedUserId) } : {}),
      ...(caseTagIds?.length ? { [DL.caseTag]: caseTagIds.map(String) } : {}),
      ...(companyIds?.length ? { [DL.companyFilter]: companyIds.map(String) } : {}),
      ...(planningDate ? { [DL.planningDate]: String(planningDate) } : {}),
    },
  }
}

/**
 * Vue Router location to /calendar carrying its filters: the plan owner, the
 * stage tags and the companies. Like /cases, the calendar MOVES exactly these
 * and resets the rest (see {@link readCalendarArrival}).
 */
export function calendarFilterRoute({
  assignedUserId = null,
  caseTagIds = null,
  companyIds = null,
} = {}) {
  return {
    path: '/calendar',
    query: {
      ...(assignedUserId != null ? { [DL.assignedTo]: String(assignedUserId) } : {}),
      ...(caseTagIds?.length ? { [DL.caseTag]: caseTagIds.map(String) } : {}),
      ...(companyIds?.length ? { [DL.companyFilter]: companyIds.map(String) } : {}),
    },
  }
}

/** A query value that may repeat (`?k=1&k=2` → array, `?k=1` → string) → string[]. */
function queryList(raw) {
  if (raw == null) return []

  return (Array.isArray(raw) ? raw : [raw]).map(String)
}

/**
 * Vue Router location to a contact's page in /contact-messages, on its notes
 * face (default) or its chat. `notes` says the notes face OUT LOUD (`notes=1`,
 * for links that must not inherit the page's current face). `category` narrows
 * the sidebar to that contact's category door on arrival (a blank category →
 * the Uncategorized door); omit it to land on All, as before.
 */
export function contactMessagesRoute({ contactId, chat = false, notes = false, category } = {}) {
  return {
    name: 'contact-messages',
    query: {
      [DL.contact]: String(contactId),
      ...(chat ? { chat: '1' } : notes ? { notes: '1' } : {}),
      ...(category !== undefined ? { [DL.contactCategory]: category ? String(category) : DL_UNCATEGORIZED } : {}),
    },
  }
}

/**
 * Vue Router location to Add/Import Case seeded from a directory contact —
 * the /contacts card's "Create Case" (contact → caregiver conversion). On
 * arrival the page pinpoint-fetches the contact and pre-fills the caregiver
 * side of the form (name, phone, email, org picks).
 */
export function caseImportFromContactRoute({ contactId }) {
  return {
    path: '/admin/case-import',
    query: { contactId: String(contactId) },
  }
}

// ── Parsers (target pages) ───────────────────────────────────────────────────

/**
 * The Followups page's "arrival intent" from the route query. Returns the raw
 * values PLUS the two named rules the page acts on:
 *   - `isAssigneeHandoff` → came from /cases (or /calendar via /cases) WITH an
 *      assignee filter: view AS that user.
 *   - `isForeignCaseDeepLink` → ANY OTHER deep-link to one specific case: a
 *      notification / pasted URL / search / log, OR a /cases handoff with NO
 *      assignee filter. View-as is cleared (Myself) so the case is reachable.
 */
export function readFollowupsArrival(query = {}) {
  const caseId = query[DL.case] != null ? String(query[DL.case]) : null
  const ref = query[DL.ref] != null ? String(query[DL.ref]) : null
  const viewAsUserId = query[DL.viewAsUser] != null ? String(query[DL.viewAsUser]) : null

  const isAssigneeHandoff = ref === DL_REF.currentCases && viewAsUserId != null

  return {
    caseId,
    ref,
    viewAsUserId,
    isAssigneeHandoff,
    isForeignCaseDeepLink: caseId != null && !isAssigneeHandoff,

    // Which face this arrival ASKED for (navbar chat alert, inbox cards,
    // copied links). Both are explicit now; `null` = the link said nothing, so
    // the page keeps whatever face it was on.
    openChat: query[DL.view] === DL_VIEW_CHAT,
    openNotes: query[DL.view] === DL_VIEW_NOTES,
    wantsFace:
      query[DL.view] === DL_VIEW_CHAT
        ? 'chat'
        : query[DL.view] === DL_VIEW_NOTES
          ? 'notes'
          : null,
  }
}

/**
 * The /contact-messages arrival intent: the contact to open, and the category
 * door asked for (null = none asked → All).
 */
export function readContactMessagesArrival(query = {}) {
  const raw = query[DL.contactCategory]

  return {
    contactId: query[DL.contact] != null ? String(query[DL.contact]) : null,
    category: raw != null && String(raw).trim() !== '' ? String(raw) : null,
  }
}

/**
 * The /cases page's "arrival intent" from the route query. `hasFilters` = came
 * from a filter handoff (the calendar) → MOVE these and reset everything else.
 */
export function readCasesArrival(query = {}) {
  const has = key => query[key] != null

  return {
    hasFilters: [DL.status, DL.assignedTo, DL.caseTag, DL.companyFilter, DL.planningDate].some(has),
    status: has(DL.status) ? String(query[DL.status]) : null,
    assignedUserId: has(DL.assignedTo) ? String(query[DL.assignedTo]) : null,

    // The calendar can pass several stage tags / companies (?caseTag=1&caseTag=2
    // → array; a single one → string).
    caseTagIds: queryList(query[DL.caseTag]),
    companyIds: queryList(query[DL.companyFilter]),
    planningDate: has(DL.planningDate) ? String(query[DL.planningDate]) : null,
  }
}

/**
 * The /calendar page's "arrival intent": the plan owner, stage tags and
 * companies of a filter handoff. `hasFilters` → MOVE these and reset the rest.
 */
export function readCalendarArrival(query = {}) {
  const has = key => query[key] != null

  return {
    hasFilters: [DL.assignedTo, DL.caseTag, DL.companyFilter].some(has),
    assignedUserId: has(DL.assignedTo) ? String(query[DL.assignedTo]) : null,
    caseTagIds: queryList(query[DL.caseTag]),
    companyIds: queryList(query[DL.companyFilter]),
  }
}

/**
 * Vue Router location to open ONE staff member's profile on the staff
 * follow-ups page. The link carries the staff's SCOPE, not just the id
 * (`rule_deeplink_carry_scope`: company, agency, category and status are the
 * four filters that can hide a person there) — the same query the inbox cards,
 * the incoming-call banner and the staff directory already send. Omitted
 * params are dropped. Sandy 2026-10-10: the BCBA / RBT lines of the
 * follow-ups card link here.
 *
 * The staff directory's handoff (M21) adds two more: `tag` travels even when
 * it is OFF (`tag=` → clear: the handoff is a full SET of that view's filters,
 * Sandy 2026-08-13) — pass `null` for off, leave it undefined to say nothing —
 * and `face` names the side to open (`'chat'` → `chat=1`, `'notes'` →
 * `notes=1`), the inbox's contract.
 */
export function staffProfileRoute({
  staffId = null,
  companyId = null,
  agencyId = null,
  category = null,
  status = null,
  tag,
  face = null,
} = {}) {
  return {
    name: 'staffing-followups',
    query: {
      ...(staffId != null ? { sid: String(staffId) } : {}),
      ...(companyId != null ? { [DL.company]: String(companyId) } : {}),
      ...(agencyId != null ? { [DL.agency]: String(agencyId) } : {}),
      ...(category ? { cat: String(category) } : {}),
      ...(status ? { [DL.status]: String(status) } : {}),
      ...(tag !== undefined ? { tag: tag == null ? '' : String(tag) } : {}),
      ...(face === 'chat' ? { chat: '1' } : face === 'notes' ? { notes: '1' } : {}),
    },
  }
}

/**
 * The staff profile link that also opens the Documents modal (`docs=1`) —
 * the system note "Documents received by email" links here (2026-10-12). Same
 * options as {@link staffProfileRoute}; the page opens the modal once that
 * staff member is selected.
 */
export function staffDocumentsRoute(opts = {}) {
  const route = staffProfileRoute(opts)

  return { ...route, query: { ...route.query, [DL.documents]: '1' } }
}
